import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { resolveAdminPermission } from '../../../constants/adminPermissionCatalog';
import { getEventMarketing } from '../../../api/eventMarketing';
import DsButton from '../../common/DsButton';
import { CardListSkeleton } from '../../common/ContentSkeletons';
import { MARKETING_PHASE_OPTIONS } from './eventMarketingPageParts';
import { formatItemCount, formatNumberJP } from '../../../utils/datetime';
import '../../../css/admin/events/EventMarketing.css';

export default function EventMarketingSummaryCard({ eventId, navigate }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [marketing, setMarketing] = useState(null);
  const hasUser = Boolean(user);
  const permissions = useMemo(
    () => (Array.isArray(user?.permissions) ? user.permissions : user?.permissions ? [user.permissions] : []),
    [user?.permissions],
  );
  const canView = useMemo(
    () => hasUser && resolveAdminPermission({
      role: String(user?.role || 'general').trim().toLowerCase(),
      isExternalAccount: user?.is_external_account === true,
      permissions,
      permissionKey: 'EVENT_VIEW_MARKETING',
    }),
    [hasUser, permissions, user?.is_external_account, user?.role],
  );

  useEffect(() => {
    if (!canView || !eventId) {
      setMarketing(null);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    getEventMarketing(eventId, { signal: controller.signal })
      .then((response) => setMarketing(response.marketing))
      .catch(() => {
        if (!controller.signal.aborted) setMarketing(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [canView, eventId]);

  if (!canView) return null;
  if (loading) {
    return (
      <section className="event-panel event-marketing-summary-card" id="event-marketing-section">
        <CardListSkeleton rows={2} />
      </section>
    );
  }
  if (!marketing) return null;
  const phaseLabel = MARKETING_PHASE_OPTIONS.find((option) => option.value === marketing?.phase)?.label || '下書き';
  const kpi = marketing?.primaryKpi || {};
  return (
    <section className="event-panel event-marketing-summary-card" id="event-marketing-section">
      <div className="panel-heading">
        <div><h3>マーケティング</h3></div>
        <div className="panel-actions">
          <DsButton
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/admin/workspaces/event-operations/events/${encodeURIComponent(eventId)}/marketing`)}
          >
            開く
          </DsButton>
        </div>
      </div>
      <div className="event-marketing-summary-card__grid">
        <div><span>フェーズ</span><strong>{phaseLabel}</strong></div>
        <div><span>主要KPI</span><strong>{kpi.name || '未設定'}</strong><small>{kpi.target != null ? `目標 ${formatNumberJP(kpi.target)}${kpi.unit || ''}` : '目標未設定'}</small></div>
        <div><span>施策</span><strong>{formatItemCount(marketing?.initiatives?.length || 0, '0件')}</strong></div>
      </div>
    </section>
  );
}
